import * as React from 'react'

import { Box, Button } from '@factorialco/deprecated-design-system'
import {
  ClientsTableDocument,
  ContactFragment,
  CreateFinanceContactDocument,
  FinanceContactByTaxIdDocument,
  FinanceValueObjectsContactTypeEnum,
  UpdateFinanceContactDocument,
  UpdateFinanceContactMutationVariables,
  VendorsTableDocument,
} from 'generated/resources/graphql'
import { debounce } from 'lodash'

import Select from 'modules/core/components/Select'
import TextField from 'modules/core/components/TextField'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { FormErrors, useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { countries } from 'modules/core/lib/countryList'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { ContactFormData } from './ContactFormData'

type FinanceMutationsUpdateFinanceContactArgs = UpdateFinanceContactMutationVariables

interface Props {
  contact?: ContactFragment
  onSuccess?: () => void
  contactType?: FinanceValueObjectsContactTypeEnum
}

const ContactForm = ({ contact, onSuccess, contactType }: Props) => {
  const [submitting, setSubmitting] = React.useState<boolean>(false)
  const [checkTaxId, setCheckTaxId] = React.useState<string | undefined>(
    contact?.taxId || undefined
  )
  const [existingContact, setExistingContact] = React.useState<
    { name: string; taxId?: string | null } | undefined
  >(undefined)
  const { company } = useCurrentSession()

  const [updateContact] = useMutation(UpdateFinanceContactDocument, {
    extractorFn: (data) => data.financeMutations.updateFinanceContact,
  })

  const [createContact] = useMutation(CreateFinanceContactDocument, {
    extractorFn: (data) => data.financeMutations.createFinanceContact,
  })

  const isAddressFilled = (data: ContactFormData): boolean => {
    const { addressLine1, addressLine2, city, postalCode, state } = data

    return !!(addressLine1 || addressLine2 || city || postalCode || state)
  }

  const { values, setErrors, setValues, handleSubmit, isValid, dirty } = useForm<ContactFormData>({
    enableReinitialize: true,

    defaultValues: {
      id: contact?.id ?? 0,
      taxId: contact?.taxId ?? '',
      name: contact?.name ?? '',
      legalName: contact?.legalName ?? '',
      email: contact?.email ?? '',
      phoneNumber: contact?.phoneNumber ?? '',
      addressLine1: contact?.address?.line1 ?? '',
      addressLine2: contact?.address?.line2 || undefined,
      city: contact?.address?.city || '',
      postalCode: contact?.address?.postalCode || '',
      state: contact?.address?.state || '',
      countryCode: contact?.address?.countryCode || '',
    },

    validate: (data) => {
      const errors: FormErrors<ContactFormData> = {}

      if (!data.name) {
        errors.legalName = i18n.t('common.empty')
      }

      return errors
    },

    onSubmit: async () => {
      setSubmitting(true)
      let handled = null

      if (values.id !== undefined && values.id > 0) {
        const variables: FinanceMutationsUpdateFinanceContactArgs = {
          id: values.id,
          companyId: company.id,
          taxId: values.taxId,
          legalName: values.legalName,
          address: {
            line1: values.addressLine1 ?? '',
            line2: values.addressLine2,
            city: values.city ?? '',
            postalCode: values.postalCode ?? '',
            state: values.state ?? '',
            countryCode: values.countryCode ?? '',
          },
          email: values.email ?? '',
          phoneNumber: values.phoneNumber ?? '',
        }
        const result = await updateContact({ variables })

        handled = handleResult(result, (errors) => {
          if (errors) {
            setErrors(errors)
            app.setFlash({
              type: 'wrong',
              message: i18n.t('finance.contacts.contact_form.update_contact.messages.error'),
            })
          }
        })
      } else {
        const variables = {
          // eslint-disable-next-line no-type-assertion/no-type-assertion
          contactTypes: [contactType!],
          companyId: company.id,

          taxId: values.taxId,

          legalName: values.legalName,
          // eslint-disable-next-line no-type-assertion/no-type-assertion
          name: values.name!,
          address: {
            // eslint-disable-next-line no-type-assertion/no-type-assertion
            line1: values.addressLine1!,
            line2: values.addressLine2,
            // eslint-disable-next-line no-type-assertion/no-type-assertion
            city: values.city!,
            // eslint-disable-next-line no-type-assertion/no-type-assertion
            postalCode: values.postalCode!,
            // eslint-disable-next-line no-type-assertion/no-type-assertion
            state: values.state!,
            // eslint-disable-next-line no-type-assertion/no-type-assertion
            countryCode: values.countryCode!,
          },
          email: values.email,
          phoneNumber: values.phoneNumber,
        }
        const result = await createContact({
          variables,
          awaitRefetchQueries: true,
          refetchQueries: [ClientsTableDocument, VendorsTableDocument],
        })

        handled = handleResult(result, (errors) => {
          if (errors) {
            setErrors(errors)
            app.setFlash({
              type: 'wrong',
              message: i18n.t('finance.contacts.contact_form.new_contact.messages.error'),
            })
          }
        })
      }

      setSubmitting(false)

      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('finance.contacts.contact_form.update_contact.messages.success'),
        })
        if (values.id !== undefined && values.id > 0) {
          track(trackingEvents.accounting.editContact)
        } else {
          track(trackingEvents.accounting.createContact)
        }
        onSuccess?.()
      }
      return handled
    },
  })

  const addressFilled = isAddressFilled(values)

  const { data } = useQuery(FinanceContactByTaxIdDocument, {
    variables: { companyId: company.id, taxId: checkTaxId || '' },
    skip: !company.id || !checkTaxId,
  })

  const error = existingContact
    ? i18n.t('finance.contacts.contact_form.errors.tax_id_uniqueness_error', {
        contact_name: existingContact.name,
      })
    : undefined

  React.useEffect(() => {
    if (data) {
      const fetchContact = data.finance.contactsConnection.nodes[0]
      if (fetchContact && fetchContact.id !== contact?.id) {
        setExistingContact(fetchContact)
      } else {
        setExistingContact(undefined)
      }
    }
  }, [data, contact?.id])

  const validateTaxId = (taxId: string) => {
    setCheckTaxId(taxId)
  }

  const debouncedValidation = debounce(validateTaxId, 500)

  return (
    <>
      <Box gap="s24">
        <TextField
          label={i18n.t('finance.contacts.contact_form.fields.name')}
          value={values.name}
          onChange={(value) => setValues({ ...values, name: value })}
          mandatory
        />
        <TextField
          label={i18n.t('finance.contacts.contact_form.fields.legal_name')}
          value={values.legalName}
          onChange={(value) => setValues({ ...values, legalName: value })}
        />
        <TextField
          label={i18n.t('finance.contacts.contact_form.fields.tax_identification_number')}
          value={values.taxId}
          onChange={(value) => {
            setValues({ ...values, taxId: value })
            debouncedValidation(value)
          }}
          error={error}
        />
        <Select
          none={i18n.t('common.select')}
          value={values.countryCode?.toLowerCase()}
          label={i18n.t('finance.contacts.contact_form.fields.country')}
          options={countries(i18n.locale)}
          onChange={(value) => setValues({ ...values, countryCode: value })}
        />
        <TextField
          label={i18n.t('finance.contacts.contact_form.fields.address_line_1')}
          value={values.addressLine1}
          onChange={(value) => setValues({ ...values, addressLine1: value })}
          {...(addressFilled && { mandatory: true })}
        />
        <TextField
          label={i18n.t('finance.contacts.contact_form.fields.address_line_2')}
          onChange={(value) => setValues({ ...values, addressLine2: value })}
          value={values.addressLine2}
        />
        <TextField
          label={i18n.t('finance.contacts.contact_form.fields.city')}
          value={values.city}
          onChange={(value) => setValues({ ...values, city: value })}
          {...(addressFilled && { mandatory: true })}
        />
        <TextField
          label={i18n.t('finance.contacts.contact_form.fields.zip_code')}
          value={values.postalCode}
          onChange={(value) => setValues({ ...values, postalCode: value })}
          {...(addressFilled && { mandatory: true })}
        />
        <TextField
          label={i18n.t('finance.contacts.contact_form.fields.state_province_region')}
          value={values.state}
          onChange={(value) => setValues({ ...values, state: value })}
          {...(addressFilled && { mandatory: true })}
        />
        <TextField
          label={i18n.t('finance.contacts.contact_form.fields.email')}
          value={values.email}
          onChange={(value) => setValues({ ...values, email: value })}
        />
        <TextField
          label={i18n.t('finance.contacts.contact_form.fields.phone_number')}
          value={values.phoneNumber}
          onChange={(value) => setValues({ ...values, phoneNumber: value })}
        />
        {dirty && (
          <Box alignItems="flexEnd">
            <Button onClick={() => handleSubmit()} disabled={!isValid || submitting}>
              {i18n.t('common.submit')}
            </Button>
          </Box>
        )}
      </Box>
    </>
  )
}

export default ContactForm
